import useSWRFetcher from "./fetcher";
import useSWR from "swr";
import settings from "../../../constants/settings";
import { pageFailure } from "../../../reducers/uploadedBoard";
import {
  BoardUploadFileType,
  DownloadableFileResponse,
  UploadFilesResponse,
  useUploadBoardFiles,
} from "../uploadToS3";
import { UserSummary } from "../../../reducers/models/users";
import useSendBoardEvent, {
  BoardTransitionEvent,
  MachineGroup,
} from "./uploaded-board/state-machine/sendBoardEvent";
import { useContext } from "react";
import { useAppDispatch } from "../store";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "./uploaded-board/state-machine/reviewBoardFlowLoader";
import { useCustomConfirm } from "../alerts";

export enum MessageAuthorOptions {
  FLASHPCB_ENGINEER = "FlashPCB Engineer",
  CUSTOMER = "Customer",
  SYSTEM = "System",
}

export enum MessageStatus {
  READ = "Read",
  UNREAD = "Unread",
}

export interface BoardMessage {
  id: string;
  board_id: string;
  author_id: string | null;
  timestamp: string;
  designators: string[];
  message: string;
  author_type: MessageAuthorOptions;
  status: MessageStatus;
  internal_only: boolean;
  file_attachments: DownloadableFileResponse[];
}

interface BoardMessagesResponse {
  messages: BoardMessage[];
  authors: Record<string, UserSummary>;
}

export function useGetBoardMessages(boardId: string) {
  const fetcher = useSWRFetcher<BoardMessagesResponse>();

  const { data, mutate } = useSWR(
    `${settings.api_url}/boards/${boardId}/messages`,
    fetcher,
  );

  return {
    messagesResponse: data || null,
    refreshMessages: mutate,
  };
}

export interface CustomerMessageFormValues {
  designators: string[];
  message: string;
  internalOnly: boolean;
}

export function useCustomerMessage(onSave: () => void) {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();
  const { sendBoardEvent, loading } = useSendBoardEvent();
  const errorHandler = () => {
    dispatch(pageFailure({ boardId }));
  };
  const { uploadBoardFiles, sending } = useUploadBoardFiles(
    errorHandler,
    BoardUploadFileType.AUXILIARY,
  );
  const customConfirm = useCustomConfirm();

  const saveCustomerMessage = (
    boardId: string,
    values: CustomerMessageFormValues,
    files: File[],
  ) => {
    const saveMessageAfterUpload = async (fileLinks: UploadFilesResponse) => {
      await sendBoardEvent(boardId, {
        machine_group: MachineGroup.BOARD,
        model_id: boardId,
        event: BoardTransitionEvent.SEND_MESSAGE,
        update: {
          message_to_add: {
            designators: values.designators,
            message: values.message,
            internal_only: values.internalOnly,
            files: fileLinks.upload_data.map((uploadDate) => ({
              file_name: uploadDate.file_name,
              s3_key: uploadDate.s3_key,
            })),
          },
        },
      });
      onSave();
    };
    uploadBoardFiles(boardId, files, saveMessageAfterUpload);
  };

  const deleteMessage = (boardId: string, messageId: string) => {
    customConfirm("Are you sure you want to delete this message?", async () => {
      await sendBoardEvent(boardId, {
        machine_group: MachineGroup.BOARD,
        model_id: boardId,
        event: BoardTransitionEvent.DELETE_MESSAGE,
        update: { message_id: messageId },
      });
      onSave();
    });
  };

  return { saveCustomerMessage, deleteMessage, saving: loading || sending };
}
